/**
 * ProjectToolsCard — compact integration-status sidebar card for the Overview dashboard.
 *
 * Renders the five auto-provisioned integrations (GitHub, Slack, Jira, Clockify, SharePoint) as a
 * dense list: icon + name + a colour-coded status dot (green linked / amber pending / red failed /
 * grey skipped). Linked tools with an external URL expose an inline link. This is the read-only,
 * at-a-glance mirror of the full ToolsSection (Settings tab) — no manual-fix form here.
 */
import {
  Clock,
  ExternalLink,
  GitBranch,
  Globe,
  Hash,
  ListChecks,
  type LucideIcon,
} from "lucide-react";

import type { Project, ProvisioningState, ProvisioningTool } from "@/types";
import { cn } from "@/lib/utils";
import { sharePointLocationUrl } from "@/lib/linkage";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface ToolConfig {
  key: ProvisioningTool;
  label: string;
  Icon: LucideIcon;
  href: (p: Project) => string | null;
}

const TOOLS: ToolConfig[] = [
  {
    key: "github",
    label: "GitHub",
    Icon: GitBranch,
    href: (p) =>
      p.github_url ??
      (p.github_repo
        ? `https://github.com/Cloudelligent/${p.github_repo}`
        : null),
  },
  { key: "slack", label: "Slack", Icon: Hash, href: () => null },
  {
    key: "jira",
    label: "Jira",
    Icon: ListChecks,
    href: (p) => p.jira_project_url ?? null,
  },
  {
    key: "clockify",
    label: "Clockify",
    Icon: Clock,
    href: (p) => p.clockify_project_url ?? null,
  },
  {
    key: "sharepoint",
    label: "SharePoint",
    Icon: Globe,
    href: (p) =>
      sharePointLocationUrl(p.sharepoint_site_url, p.sharepoint_folder_path),
  },
];

/**
 * Status dot colour + spoken word per provisioning state (WCAG: never colour alone).
 *
 * This is a total `Record<ProvisioningState, ...>` — every state MUST have an entry. A missing key
 * is not a type error at the lookup site (`STATE_DOT[state]` is typed as always-present), so a gap
 * surfaces only at runtime as `undefined.dot` and crashes the whole Overview render. Guarded by
 * `src/types/__tests__/provisioning-state-mirror.test.ts`.
 */
const STATE_DOT: Record<ProvisioningState, { dot: string; word: string }> = {
  linked: { dot: "bg-success", word: "Linked" },
  pending: { dot: "bg-[#c98a00]", word: "Pending" },
  // In-flight: same amber family as `pending` but animated, so "queued" and "actively working"
  // are distinguishable at a glance without relying on colour alone (the aria-label differs too).
  provisioning: { dot: "bg-[#c98a00] animate-pulse", word: "Provisioning…" },
  failed: { dot: "bg-error", word: "Failed" },
  skipped: { dot: "bg-text-disabled", word: "Skipped" },
  // User-cancelled auto-setup: neutral like 'skipped' (nothing is running), distinct word so it is not
  // mistaken for "integration not configured".
  cancelled: { dot: "bg-text-disabled", word: "Cancelled" },
};

/**
 * Resolve the effective state for a tool: explicit provisioning_status wins, otherwise infer
 * "linked" from the presence of a resolvable link (mirrors ToolsSection.resolveState).
 */
function resolveState(project: Project, tool: ToolConfig): ProvisioningState {
  const explicit = project.provisioning_status?.[tool.key]?.state;
  if (explicit) return explicit;
  // SharePoint has no dedicated link resolver in TOOLS.href for inference beyond the URL itself.
  return tool.href(project) ? "linked" : "pending";
}

function ToolRow({
  project,
  tool,
}: {
  project: Project;
  tool: ToolConfig;
}): JSX.Element {
  const state = resolveState(project, tool);
  // Safety net, not the real guard: `provisioning-state-mirror.test.ts` is what keeps STATE_DOT
  // total. This `??` exists because the failure mode of a missing key was a thrown TypeError that
  // unmounted the ENTIRE Overview page. Degrading to a neutral dot is strictly better than that.
  const status = STATE_DOT[state] ?? { dot: "bg-text-disabled", word: state };
  const href = tool.href(project);
  const showLink = state === "linked" && Boolean(href);

  return (
    <li className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
      <tool.Icon
        className="h-4 w-4 shrink-0 text-text-secondary"
        aria-hidden="true"
      />
      <span className="flex-1 text-sm text-text-primary">{tool.label}</span>

      {showLink && href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-secondary transition-colors hover:text-primary"
          aria-label={`Open ${tool.label}`}
          title={`Open ${tool.label}`}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}

      <span
        className={cn("h-2 w-2 shrink-0 rounded-full", status.dot)}
        role="img"
        aria-label={`${tool.label}: ${status.word}`}
        title={status.word}
      />
    </li>
  );
}

export interface ProjectToolsCardProps {
  project: Project;
}

export function ProjectToolsCard({
  project,
}: ProjectToolsCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <GitBranch className="h-4 w-4 text-primary" aria-hidden="true" />
          Integrations
        </h3>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-outlines">
          {TOOLS.map((tool) => (
            <ToolRow key={tool.key} project={project} tool={tool} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default ProjectToolsCard;
